import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Compass, FileText, ScanText, ListChecks, Video, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { get } from "@/lib/api";
import { MODULES, ROLE_LABELS, STAFF, canSee } from "@/lib/nav";
import { Logo } from "@/components/app/Sidebar";
import { toast } from "sonner";
import { PageTour } from "@/components/app/PageTour";

const STEPS = [
  { icon: FileText, title: "Post a job", text: "Paste or upload the JD" },
  { icon: ScanText, title: "AI reads it", text: "Requirements you can edit" },
  { icon: ListChecks, title: "Screen resumes", text: "Ranked by Fit Score" },
  { icon: Video, title: "Assess and interview", text: "Personality and AI interviews" },
  { icon: Trophy, title: "Final ranking", text: "Hire with confidence" },
];
const SHORTCUTS = ["jobs", "candidates", "assessments", "interviews", "reports", "settings"];

function Header({ user, onSignOut }) {
  return (
    <header className="flex items-center justify-between max-sm:flex-wrap max-sm:gap-2 py-4">
      <Link to="/" data-testid="landing-logo"><Logo /></Link>
      {user ? (
        <div className="flex items-center gap-4 text-sm" data-testid="landing-user-info">
          <span className="text-ink">{user.name}</span>
          <span className="rounded-full border border-line bg-white px-2.5 py-0.5 text-xs text-ink-secondary">{ROLE_LABELS[user.role]}</span>
          <button data-testid="landing-sign-out-btn" onClick={onSignOut} className="text-ink-secondary hover:text-ink transition-colors duration-150">Sign out</button>
        </div>
      ) : (
        <Button asChild variant="outline" data-tour="pt-landing-sign-in-btn" data-testid="landing-sign-in-btn"><Link to="/login">Sign in</Link></Button>
      )}
    </header>
  );
}

function StepsRow() {
  return (
    <ol className="grid grid-cols-5 gap-3 max-lg:grid-cols-3 max-md:grid-cols-2 max-sm:grid-cols-1" data-tour="pt-how-it-works" data-testid="how-it-works">
      {STEPS.map((s, i) => (
        <li key={s.title} className="panel flex flex-col gap-2 p-4">
          <div className="flex items-center gap-2">
            <span className="font-display text-sm font-semibold text-brand">{i + 1}</span>
            <s.icon size={16} className="text-ink-muted" />
          </div>
          <p className="text-sm font-medium text-ink">{s.title}</p>
          <p className="text-xs text-ink-secondary leading-snug">{s.text}</p>
        </li>
      ))}
    </ol>
  );
}

export default function Landing() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { data: brand } = useQuery({ queryKey: ["public-branding"], queryFn: () => get("/public/branding") });
  const { data: summary } = useQuery({ queryKey: ["dashboard"], queryFn: () => get("/dashboard/summary"), enabled: !!user });
  const company = !brand?.company_name || /\bdemo\b/i.test(brand.company_name) ? "your law firm" : brand.company_name;
  const go = (target) => nav(user ? target : `/login?next=${encodeURIComponent(target)}`);
  const role = user?.role;
  const shortcuts = SHORTCUTS.map((k) => MODULES.find((m) => m.key === k)).filter((m) => !user || canSee(m, role));
  const canCreate = !user || STAFF.includes(role);
  const signOut = async () => { await logout(); toast.success("You've been signed out"); };

  return (
    <div className="min-h-screen bg-canvas">
      <div className="mx-auto flex min-h-screen max-w-[960px] flex-col px-6 max-sm:px-4">
        <Header user={user} onSignOut={signOut} />
        <main className="flex flex-1 flex-col justify-center gap-6 py-2 tl-page">
          <section className="tl-hero space-y-4 p-6 sm:p-8" data-testid="landing-hero-card">
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink-muted" data-testid="landing-positioning">Legal Hiring Operating System</p>
            <h1 className="max-w-3xl font-display text-4xl font-medium leading-[1.12] max-sm:text-[28px] tracking-[-0.02em] text-ink lg:text-[46px]" data-tour="pt-landing-headline" data-testid="landing-headline">
              Screen, assess and interview legal talent for {company}
            </h1>
            <p className="max-w-2xl text-base text-ink-secondary leading-relaxed">
              Onboard is the Legal Hiring Operating System for law firms and legal teams. It reads your job descriptions,
              ranks applicants on practice area, PQE and Bar status, and runs structured assessments and AI interviews.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Button data-tour="pt-landing-open-dashboard-btn" data-testid="landing-open-dashboard-btn" onClick={() => go("/app")}>Open dashboard <ArrowRight size={16} /></Button>
              {canCreate && <Button variant="outline" data-testid="landing-create-job-btn" onClick={() => go("/app/jobs/new")}>Create a job</Button>}
              <PageTour />
              <button data-testid="landing-tour-btn" onClick={() => go("/app?tour=1")} className="ml-1 inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-brand transition-colors duration-150">
                <Compass size={16} /> Take the product tour
              </button>
            </div>
          </section>
          <section className="space-y-3">
            <h2 className="text-base font-semibold text-ink">How it works</h2>
            <StepsRow />
          </section>
          <section className="space-y-3">
            <h2 className="text-base font-semibold text-ink">Go to a module</h2>
            <div className="grid grid-cols-3 gap-3 max-md:grid-cols-2 max-sm:grid-cols-1" data-tour="pt-module-shortcuts" data-testid="module-shortcuts">
              {shortcuts.map((m) => (
                <button key={m.key} data-testid={`module-shortcut-${m.key}`} onClick={() => go(m.path)}
                  className="panel tl-lift group flex cursor-pointer items-center gap-3 px-4 py-3 text-left hover:border-brand/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">
                  <m.icon size={18} className="text-brand" />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-ink">
                      {m.label}
                      {m.key === "jobs" && user && summary && <span className="ml-2 rounded-full bg-brand-tint px-2 py-0.5 text-xs text-brand" data-testid="landing-open-jobs-count">{summary.open_jobs} open</span>}
                    </span>
                    <span className="block text-xs text-ink-muted">{m.blurb}</span>
                  </span>
                  <ArrowRight size={16} className="text-ink-muted transition-transform duration-150 group-hover:translate-x-0.5" />
                </button>
              ))}
            </div>
          </section>
        </main>
        <footer className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line py-3 text-xs text-ink-muted" data-testid="landing-footer">
          <Link to="/privacy" className="hover:text-ink" data-testid="footer-privacy-link">Candidate privacy notice</Link>
          <span>|</span>
          <span>Support: <a href={`mailto:${brand?.support_email || "support@onboardhire.in"}`} className="hover:text-ink">{brand?.support_email || "support@onboardhire.in"}</a></span>
        </footer>
      </div>
    </div>
  );
}
